import { QueryClient, MutationCache } from "@tanstack/react-query";
import { Logger } from "@/lib/logger";
/**
 * Minimum time the tab must have been hidden before a focus event is
 * allowed to refetch active queries. Quick clicks between windows or
 * brief tab-aways shouldn't trigger a full revalidation pass across
 * every active query — only meaningful absences (the user came back to
 * the viewer after actually doing something else) should.
 */
const FOCUS_REFETCH_AFTER_HIDDEN_MS = 60_000;
let lastHiddenAt;
let armedForRefetch = false;
if (typeof document !== "undefined") {
    // Listener registered at module load — BEFORE `new QueryClient()` runs
    // below — so it fires before React Query's focus manager subscribes.
    // The arming has to happen first; React Query's per-query
    // `refetchOnWindowFocus` callback below reads `armedForRefetch`
    // synchronously when its own visibilitychange listener iterates
    // active queries.
    document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "hidden") {
            lastHiddenAt = Date.now();
            armedForRefetch = false;
            return;
        }
        if (lastHiddenAt === undefined)
            return;
        const elapsed = Date.now() - lastHiddenAt;
        lastHiddenAt = undefined;
        armedForRefetch = elapsed >= FOCUS_REFETCH_AFTER_HIDDEN_MS;
    });
}
/**
 * Per-query callback React Query invokes on every focus event. We arm
 * `armedForRefetch` from the visibilitychange listener above and disarm
 * in a microtask after the current focus event has propagated to every
 * active query. That way every active query in the same focus cycle
 * sees `"always"` (and refetches regardless of its individual
 * staleTime), but subsequent focuses without an intervening hide get
 * `false`.
 *
 * Returning `"always"` (instead of `true`) bypasses the per-query
 * `staleTime` check — important because individual envelope queries
 * (`block.envelope`, `app.collectionChildren`, etc.) carry their own
 * staleTime overrides that would otherwise suppress the refetch.
 */
function shouldRefetchOnFocus() {
    if (!armedForRefetch)
        return false;
    queueMicrotask(() => {
        armedForRefetch = false;
    });
    return "always";
}
export const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: 1,
            refetchOnWindowFocus: shouldRefetchOnFocus,
        },
    },
    mutationCache: new MutationCache({
        onError: (error) => {
            Logger.captureError(error instanceof Error ? error : new Error(String(error)), { source: "react-query-mutation" });
        },
    }),
});
